import React, { useEffect, useRef, useState } from 'react';
import { useApp } from '../contexts/AppContext';

const WHEEL_COLORS = ['#FF6B35', '#06D6A0', '#A855F7', '#F59E0B', '#118AB2', '#EF476F', '#FFD166', '#073B4C'];

// Every eligible student gets a slice proportional to (score + a floor), so
// nobody has literally zero chance, but a better score meaningfully raises
// the odds. This keeps the spin fair/fun while still rewarding performance.
const FLOOR = 15;

export default function Roulette() {
  const { api, lang } = useApp();
  const [programs, setPrograms] = useState([]);
  const [programId, setProgramId] = useState('');
  const [entrants, setEntrants] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [spinning, setSpinning] = useState(false);
  const [winner, setWinner] = useState(null);
  const [rotation, setRotation] = useState(0);
  const canvasRef = useRef(null);

  useEffect(() => {
    api('get', '/teacher/programs').then(res => {
      const list = res.data.filter(p => p.slug !== 'daily-routine');
      setPrograms(list);
      if (list.length) setProgramId(list[0].id);
    }).catch(() => {});
  }, [api]);

  const loadEntrants = async () => {
    if (!programId) return;
    setLoading(true);
    setError('');
    setWinner(null);
    setEntrants(null);
    try {
      const res = await api('get', `/teacher/roulette?program_id=${programId}`);
      setEntrants(res.data);
    } catch {
      setError(lang === 'ja' ? 'データを読み込めませんでした' : 'Could not load data');
    } finally {
      setLoading(false);
    }
  };

  const slices = (entrants || []).map((e, i) => ({
    ...e,
    score: Number(e.score) || 0,
    weight: (Number(e.score) || 0) + FLOOR,
    color: WHEEL_COLORS[i % WHEEL_COLORS.length],
  }));
  const totalWeight = slices.reduce((sum, s) => sum + s.weight, 0);

  useEffect(() => {
    drawWheel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entrants]);

  function drawWheel() {
    const canvas = canvasRef.current;
    if (!canvas || !slices.length) return;
    const ctx = canvas.getContext('2d');
    const size = canvas.width;
    const cx = size / 2, cy = size / 2, r = size / 2 - 6;
    ctx.clearRect(0, 0, size, size);
    let start = -Math.PI / 2;
    slices.forEach(s => {
      const angle = (s.weight / totalWeight) * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, r, start, start + angle);
      ctx.closePath();
      ctx.fillStyle = s.color;
      ctx.fill();
      ctx.strokeStyle = 'white';
      ctx.lineWidth = 3;
      ctx.stroke();

      // label
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(start + angle / 2);
      ctx.textAlign = 'right';
      ctx.fillStyle = 'white';
      ctx.font = 'bold 15px "Hiragino Sans", "Noto Sans JP", "Yu Gothic", sans-serif';
      const name = lang === 'ja' && s.name_ja ? s.name_ja : s.name;
      ctx.fillText(name.length > 10 ? name.slice(0, 9) + '…' : name, r - 14, 5);
      ctx.restore();

      start += angle;
    });
  }

  const spin = () => {
    if (!slices.length || spinning) return;
    setSpinning(true);
    setWinner(null);

    // Pick winner by weight up front, then compute a rotation that lands
    // the pointer (fixed at top, 12 o'clock) inside that slice.
    let r = Math.random() * totalWeight;
    let chosen = slices[0];
    let cumulative = 0;
    let sliceStart = 0, sliceAngle = 0;
    let angleAcc = 0;
    for (const s of slices) {
      const a = (s.weight / totalWeight) * 360;
      if (r <= cumulative + s.weight) {
        chosen = s;
        sliceStart = angleAcc;
        sliceAngle = a;
        break;
      }
      cumulative += s.weight;
      angleAcc += a;
    }
    const targetMid = sliceStart + sliceAngle / 2;
    const fullSpins = 6 * 360;
    // Canvas draws slices starting at -90deg (top) going clockwise; rotate
    // the wheel so that the chosen slice's middle ends up under the top pointer.
    const finalRotation = fullSpins + (360 - targetMid);

    setRotation(prev => prev - (prev % 360) + finalRotation);

    setTimeout(() => {
      setSpinning(false);
      setWinner(chosen);
    }, 4200);
  };

  return (
    <div className="summer-bg" style={{ minHeight: '100vh', padding: '2rem 1rem' }}>
      <div className="container" style={{ maxWidth: 900 }}>
        <div className="text-center mb-4">
          <h2 style={{ fontWeight: 900, color: 'var(--navy)' }}>
            🎡 {lang === 'ja' ? '今日のルーレット' : 'Daily Roulette'}
          </h2>
          <p style={{ color: 'var(--muted)', fontWeight: 600 }}>
            {lang === 'ja'
              ? '今日のスコアに応じて当選確率が変わる、賞品ルーレット'
              : 'Spin to pick a prize winner among today\'s stickers — better scores mean better odds, not a guarantee.'}
          </p>
        </div>

        <div className="card-summer p-4 mb-4">
          <div className="row g-3 align-items-end">
            <div className="col-md-8">
              <label className="fw-bold mb-1 d-block">{lang === 'ja' ? 'プログラム' : 'Program'}</label>
              <select className="form-select" value={programId} onChange={e => setProgramId(e.target.value)}>
                {programs.map(p => (
                  <option key={p.id} value={p.id}>{lang === 'ja' && p.name_ja ? p.name_ja : p.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <button className="btn-summer w-100" onClick={loadEntrants} disabled={loading}>
                {loading ? '...' : (lang === 'ja' ? '本日の参加者を読み込む' : 'Load Today\'s Entrants')}
              </button>
            </div>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {entrants && entrants.length === 0 && (
          <div className="text-center card-summer p-4" style={{ color: 'var(--muted)', fontWeight: 700 }}>
            {lang === 'ja'
              ? '今日、このプログラムでスタンプをもらった生徒がいません。'
              : 'No students have received a sticker in this program today yet.'}
          </div>
        )}

        {entrants && entrants.length > 0 && (
          <div className="text-center">
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <div style={{
                position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
                width: 0, height: 0, borderLeft: '14px solid transparent', borderRight: '14px solid transparent',
                borderTop: '24px solid var(--navy)', zIndex: 2,
              }} />
              <canvas
                ref={canvasRef}
                width={420}
                height={420}
                style={{
                  borderRadius: '50%',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                  transition: spinning ? 'transform 4.2s cubic-bezier(0.15, 0.75, 0.15, 1)' : 'none',
                  transform: `rotate(${rotation}deg)`,
                  maxWidth: '100%',
                }}
              />
            </div>

            <div className="mt-4">
              <button className="btn-summer" style={{ fontSize: '1.1rem', padding: '0.9rem 2.5rem' }} onClick={spin} disabled={spinning}>
                {spinning ? (lang === 'ja' ? '回転中…' : 'Spinning…') : (lang === 'ja' ? 'スピン！' : 'SPIN!')}
              </button>
            </div>

            {winner && !spinning && (
              <div className="card-summer p-4 mt-4 mx-auto" style={{ maxWidth: 420, borderTop: `6px solid ${winner.color}` }}>
                <div style={{ fontSize: '2.5rem' }}>🏆</div>
                <h3 style={{ fontWeight: 900, color: 'var(--navy)', margin: '0.5rem 0' }}>
                  {lang === 'ja' && winner.name_ja ? winner.name_ja : winner.name}
                </h3>
                <p style={{ color: 'var(--muted)', fontWeight: 600 }}>
                  {lang === 'ja' ? `本日のスコア: ${winner.score}%` : `Today's score: ${winner.score}%`}
                </p>
              </div>
            )}

            <div className="mt-4 d-flex flex-wrap justify-content-center gap-2">
              {slices.map(s => (
                <span key={s.id} className="badge" style={{ background: s.color, fontWeight: 700, padding: '0.5rem 0.8rem' }}>
                  {lang === 'ja' && s.name_ja ? s.name_ja : s.name} — {s.score}%
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
